<script setup lang="ts">
import type { HTMLAttributes } from "vue";
import { cn } from "@/shadcn-lib/utils";
import { inject, computed } from "vue";
import { ChevronUp } from "lucide-vue-next";

interface AccordionContext {
  type: "single" | "multiple";
  value: import("vue").Ref<string[]>;
  onValueChange: (value: string[]) => void;
  collapsible?: boolean;
}

const props = defineProps<{
  class?: HTMLAttributes["class"];
  disabled?: boolean;
}>();

const context = inject<AccordionContext>("accordion-context");

const itemContext = inject<{
  value: string;
  open: import("vue").ComputedRef<boolean>;
  disabled?: boolean;
}>("accordion-item-context");

const disabled = computed(() => props.disabled || itemContext?.disabled);

const onClick = () => {
  if (disabled.value) return;

  if (!context || !itemContext) return;

  const currentValue = context.value.value;
  const itemValue = itemContext.value;

  if (context.type === "single") {
    if (context.collapsible && currentValue.includes(itemValue)) {
      context.onValueChange([]);
    } else {
      context.onValueChange([itemValue]);
    }
  } else {
    if (currentValue.includes(itemValue)) {
      context.onValueChange(currentValue.filter((v) => v !== itemValue));
    } else {
      context.onValueChange([...currentValue, itemValue]);
    }
  }
};
</script>

<template>
  <button
    type="button"
    :disabled="disabled"
    :data-state="itemContext?.open.value ? 'open' : 'closed'"
    data-slot="accordion-trigger"
    :class="
      cn(
        'flex flex-1 items-center justify-between py-4 text-left font-medium transition-all hover:underline',
        props.class
      )
    "
    @click="onClick"
  >
    <slot />
    <ChevronUp
      :class="cn('w-4 h-4 transition-transform duration-200 shrink-0', { 'rotate-180': itemContext?.open.value })"
    />
  </button>
</template>
